import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "动漫", to: "/category/anime" },
  { label: "综艺", to: "/category/variety" },
  { label: "纪录片", to: "/category/documentary" },
  { label: "专题", to: "/special/deep-space-dialogue" }
];

export default function SiteNavbar() {
  const [query, setQuery] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setQuery("");
  }, [location.pathname]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const keyword = query.trim();
    if (!keyword) return;
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
    setMobileOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-800 bg-background-950/90 backdrop-blur-md">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link
          to="/"
          className="flex shrink-0 cursor-pointer items-center gap-2"
          aria-label="秘密影院首页"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-accent-400">
            <i className="ri-movie-2-fill text-lg" aria-hidden="true" />
          </span>
          <span className="leading-none">
            <span className="block font-heading text-lg font-700 tracking-wide text-foreground-50">
              秘密影院
            </span>
            <span className="hidden text-[10px] tracking-wider text-foreground-500 md:block">
              SECRET CINEMA
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-500 transition-colors ${
                isActive(item.to)
                  ? "bg-background-800 text-accent-400"
                  : "text-foreground-300 hover:bg-background-900 hover:text-foreground-50"
              }`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden w-44 md:block lg:w-64"
          role="search"
        >
          <div className="flex items-center gap-2 rounded-full border border-background-700 bg-background-900 px-3 py-1.5 transition-colors focus-within:border-secondary-500">
            <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm" aria-hidden="true" />
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索影视作品"
              aria-label="搜索影视作品"
              className="w-full bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开导航菜单"
          className="ml-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 lg:hidden"
        >
          <i
            className={mobileOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}
            aria-hidden="true"
          />
        </button>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-800 bg-background-950/98 px-4 py-4 lg:hidden animate-fade-in">
          <form onSubmit={submitSearch} role="search" className="mb-4 md:hidden">
            <div className="flex items-center gap-2 rounded-full border border-background-700 bg-background-900 px-4 py-2 focus-within:border-secondary-500">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" aria-hidden="true" />
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索影视作品"
                aria-label="搜索影视作品"
                className="w-full bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none"
              />
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={`cursor-pointer rounded-md px-4 py-3 text-sm font-500 transition-colors ${
                  isActive(item.to)
                    ? "bg-background-800 text-accent-400"
                    : "bg-background-900 text-foreground-200"
                }`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}